(ns frontend.main
  (:require
   [cljsjs.device]
   [clojure.string :as string]
   [cognitect.transit :as t]
   [common.dates :as dates]
   [common.features.plan :as plan]
   [common.localization :as loc]
   [common.roles :as roles]
   [common.schema.entities.survey.survey-reference-periods :as survey-reference-periods]
   [common.schema.entity-types :as entity-types]
   [common.themes.registry :as themes-registry]
   [common.time :as c-time]
   [common.timer :as timer]
   [common.util :as cu]
   [comms.sente :as sente]
   [frontend.account :as account]
   [frontend.actions.account :as account-actions]
   [frontend.browser-utils :as bu]
   [frontend.conn :as conn]
   [frontend.db :as db]
   [frontend.device-id :as device-id]
   [frontend.events :as event-handlers]
   [frontend.local-storage :as ls]
   [frontend.logging :as logging]
   [frontend.maintenance :as maintenance]
   [frontend.routing :as routing]
   [frontend.shared.state :as shared-state]
   [frontend.state.current-session :as session]
   [frontend.state.updates :as state-updates]
   [frontend.subscription :as subscription]
   [frontend.updates :as updates]
   [frontend.ui.component.chart.view :as chart-view]
   [frontend.ui.component.file-input.event]
   [frontend.ui.style.theme.core :as theme]
   [frontend.utils :as fu]
   [frontend.view.components.clipboard :as clipboard]
   [frontend.view.login.events :as login]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.session-timeout.view :as session-timeout]
   [frontend.view.modals.view :as new-modals]
   [frontend.view.profile.events :as profile-events]
   [frontend.view.views]
   [frontend.zap-time :as zap-time]
   [fx.dev :as dev]
   [goog.dom :as dom]
   [potpuri.core :refer [fn->>]]
   [re-frame.core :as rf]
   [reagent-dev-tools.core :as dev-tools]
   [reagent.core :as r]
   [reagent.dom :as rd]
   [sentry.integration :as sentry]
   [ui.components.button.view :as button]
   [ui.components.chart.view :as chart]
   [ui.components.notification.view :as notification]
   [ui.components.toast.view :as toast]
   [ui.dom-globals :as dg]
   [ui.fx.fetch]
   [ui.tailwind-theme :as tailwind-theme]
   [ui.view.sheets.view :as sheets-view]
   [ui.window :as window]
   [z-eines.impl :as eines-impl]))

;; Used for worker.js timestamp to prevent caching - set via shadow-cljs during build time:
(goog-define BUILDTIMEMILLIS 0)

;; (defn sandbox-popover
;;   []
;;   (r/as-element
;;    [rbs/Popover
;;     {:placement "bottom"
;;      :id        "sandbox-popover"
;;      :title     ""}
;;     [:div
;;      [:p
;;       "This is your test environment. Any changes here will be deleted later
;;                       so you can play around freely in this environment. To switch between
;;                       this test environment and your real data, please navigate to the rightmost menu
;;                       in the black main navigation bar below and click 'Switch to your data'"]]]))

(defn sandbox-infobar
  []
  [:div
   {:class "p-3 bg-info-light border border-solid border-info-light-border text-info-light-text rounded"}
   (if (roles/is-guest-role? @session/role)
     "This is a sample environment. All data here will be discarded later."
     [:p
      "Test environment "
      (when-not (session/is-guest?)
        [button/secondary-open
         [:i.fal.fa-info-circle
          {:aria-hidden true}]])])])

;; (defn pending-updates-bar
;;   [items]
;;   [:div.alert.alert-info
;;    [:ul
;;     (->> items
;;          (map (fn [[id entity-updates]]
;;                 [:li (pr-str entity-updates)]))
;;          (doall))]])

(defn failed-updates-bar
  [items]
  [:div
   {:class "relative p-3 bg-info-light border border-solid border-info-light-border text-info-light-text rounded"}
   [button/square-secondary-open
    {:type       "button"
     :aria-label (loc/tr :close)
     :class      "absolute top-2 right-2 p-1 text-info-light-text-pale hover:text-info-light-text border-0 w-auto h-auto"
     :on-click   (fn [_]
                   (reset! session/failed-updates {}))}
    [:i.fal.fa-times {:aria-hidden true}]]
   [:h1 {:class "pr-8"} (loc/tr :some-requests-failed)]
   [:ul
    (->> items
         (map (fn [[id entity-updates]]
                ^{:key (str "failed-updates-" id)}
                [:li (pr-str entity-updates)]))
         (doall))]])

;;TODO generalize this when introducing other error bars
(defn nylas-error-bar
  []
  (let [nylas-error-bar-statuses [:nylas.status/error :nylas.status/disconnected]
        nylas-status @session/account-nylas-status
        loaded? @session/tenant-data-loaded?
        show? (and loaded?
                   (session/user?)
                   (plan/available :feature/nylas-integration @session/tenant @session/role)
                   (some #{nylas-status} nylas-error-bar-statuses))
        account-id @session/account-id
        msg {:db/id                account-id
             :account/nylas-status :nylas.status/error-dismissed}
        dismiss! (fn [_]
                   ;;optimistic update:
                   (swap! session/account assoc :account/nylas-status :nylas.status/error-dismissed)
                   (account-actions/*->update-personal
                    msg
                    (fn [])
                    (fn [err]
                      (logging/error (str "error! updating nylas status " err))
                       ;;retract the optimistic update above:
                      (swap! session/account assoc :account/nylas-status nylas-status))))]
    (when show?
      [:div
       {:class "p-3 mx-auto flex items-center gap-3 text-warning-light-text bg-warning-light border border-solid border-warning-light-border rounded
                fixed top-3 left-0 right-0 w-11/12 md:w-6/12 z-50"}
       [:span (loc/tr :nylas-connect/error-bar-message)]
       [button/sm-primary-solid
        {:on-click profile-events/connect-nylas
         :variant  "link"}
        (loc/tr :personal-profile/email-sync-reconnect-account-button)]
       [:span
        {:class "absolute top-2 right-2 cursor-pointer hover:text-warning-light-text-accent"}
        [:i.fal.fa-times
         {:on-click dismiss!}]]])))

(defn main-view
  []
  (r/create-class
   {:reagent-render (fn []
                      (let [account-state @(r/track account/account-state)
                            account-role @(r/track account/role)
                            view @(r/track routing/view)
                            route-view
                            @(r/track routing/route-view account-state account-role view)
                            mobile @session/device-type
                             ;_ (js/console.log "MAIN -->>>> " (clj->js view))
                            hiccup [:div
                                    {:class-name (fu/cs "main-view"
                                                        @db/global-cursor
                                                        (when @db/hide-modals
                                                          "hide-modals")
                                                        (when @db/pipeline-drag
                                                          "pipeline-drag")
                                                        (when (some #{mobile} ["ios" "android"])
                                                          "mobile")
                                                        (when (bu/FF?)
                                                          "firefox")
                                                        (-> (:tenant.inbox/email @session/tenant)
                                                            (str)
                                                            (string/lower-case))
                                                        (str "progress-" @session/sync-progress))}
                                    [clipboard/view @session/clipboard]
                                    [notification/view]
                                    [toast/toaster]

                                    #_[:div
                                       {:class-name (str "connection-problem"
                                                         (when (= conn-state :closed) " active"))}
                                       [:p
                                        [:i.fal.fa-spinner.fa-pulse]
                                        (str " " (loc/tr :connection-problems))]]

                                    (when (logging/print-debug?)
                                      (when-let [failed-updates (seq @session/failed-updates)]
                                        [failed-updates-bar failed-updates]))

                                    [:div
                                     {:class "flex flex-col h-full w-full relative"}
                                     (when (:tenant/sandbox? @session/tenant)
                                       [sandbox-infobar])

                                     [nylas-error-bar]
                                      ;(js/console.log "RENDER VIEW : " route-view)
                                     [routing/render-view route-view]]
                                    [new-modals/view]
                                    [sheets-view/sheet-stack]]]
                        (when (or (session/sys-admin?)
                                  (session/dev?))
                          (logging/log
                           "MAIN VIEW " (- (c-time/now-millis) @db/start-time)
                           " < " route-view))
                        (if (and @session/maintenance
                                 (not @session/role))
                          (maintenance/view)
                          hiccup)))}))

(defn render-dev-tools
  []
  (when (and goog.DEBUG (nil? (dg/element-by-id "dev")))
    (let [el (dom/createDom "div" (clj->js {"id" "dev"}))]
      (dom/appendChild (dg/body) el)
      (dev-tools/start! {:el              el
                         :state-atom      db/app-state
                         :state-atom-name "app-state"}))))

(defn track-session-time
  []
  (let [expires-at (:expires-at @session/session)
        now (c-time/now-millis)
        expires-in (when expires-at
                     (- expires-at now))
        view (routing/view)
        requires-login? (routing/page-requires-login? view)]
    (when expires-in
      (when (and requires-login?
                 (< expires-in (* 1000 30))
                 (->> @db/modals
                      (vals)
                      (cu/filter-by :modal-type session-timeout/modal-key)
                      (not-any? :shown?)))
        (modals/open-modal! session-timeout/modal-key {}))
      (when (and requires-login?
                 (neg? expires-in))
        (logging/info "Logout >> TIMEOUT")
        (logging/error "TIMEOUT LOGOUT "
                       {:now        now
                        :expires-at expires-at
                        :expires-in expires-in})
        (account/reset-session)))))

(defn check-accesses
  []
  (let [start (c-time/now-millis)
        test-img (js/Image.)]
    (aset test-img
          "onload"
          (fn [_]
            ;; no privacy mode
            ))
    (aset test-img
          "onerror"
          (fn [_]
            (when (< (- (c-time/now-millis) start) 2000)    ;; if it took more than 2000ms we assume network error
              (logging/log "Tracking protection")
              (swap! db/app-state assoc :tracking-protection true))))
    (aset test-img "src" "/favicon.ico")))

(defn calculate-browser-sizes
  []
  (let [scroll-container (dg/element-by-id "scroll-container")
        scroll-child (dg/element-by-id "scroll-child")
        no-scroll-container (dg/element-by-id "no-scroll-container")
        no-scroll-child (dg/element-by-id "no-scroll-child")
        w1 (some-> scroll-child
                   (dg/offset-width))
        w2 (some-> no-scroll-child
                   (dg/offset-width))]
    (if (js/isNaN (- w1 w2))
      (logging/error "CANNOT CALCULATE SCROLL WIDTH : w1 " w1 " w2 " w2)
      (do
        (when (and scroll-container (dg/parent-node scroll-container))
          (dg/remove-child (dg/parent-node scroll-container) scroll-container))
        (when (and no-scroll-container (dg/parent-node no-scroll-container))
          (dg/remove-child (dg/parent-node no-scroll-container) no-scroll-container))
        (swap! db/app-state assoc-in [:browser :scrollbar-width] (- w2 w1))))))

(defn track-window-size
  []
  (let [height (.-innerHeight js/window)
        width (.-innerWidth js/window)]
    (swap! db/window assoc
           :height height
           :width width)))

(defn conn-listener
  [status]
  (logging/info "Socket connection " status))

(def reconnecting? (atom false))

(defn on-connected!
  []
  ;; TODO - rethink application lifecycle, when disconnected + reconnected, the initialization might not work correctly
  (let [loc (.-pathname js/window.location)
        mobile? (string/starts-with? loc "/login/mobile/")
        token (when mobile?
                (-> loc
                    (string/split #"/")
                    last))]
    (dev/init!)
    ;; Login workflow after socket connection has opened - the app starts here!
    (login/init! token
                 (fn []
                   (conn/has-valid-session!)
                   (when (session/dev?)
                     (render-dev-tools))))

    (when-not (:session-timeout-interval @db/app-state)
      (swap! db/app-state assoc :session-timeout-interval (js/setInterval track-session-time 5000)))

    (when-not (:window-resize-listener @db/app-state)
      (swap! db/app-state assoc
             :window-resize-listener
             (.addEventListener js/window "resize" track-window-size))
      (track-window-size))

    (when-not @reconnecting?
      (rd/render [main-view] (dg/element-by-id "zap-application"))
      (reset! reconnecting? true))))

;; Note: only task-emails (via :task/relates) should be saved to session/entities & to indexeddb
;;       (other's are handled via entity-viewer subscriptions & entity-viewer's local state)
(defn prepare-for-indexeddb
  [entities-by-id]
  (let [filtered (->> entities-by-id
                      (cu/remove-kv (fn->> second
                                           :entity/type
                                           (contains? entity-types/dont-save-to-frontend-cache))))]
    ;; add other needed preprocessing:
    filtered))

(defn post-snapshot-data!
  [worker now]
  (when @session/use-indexeddb?
    (when-not @session/reset-sync?
      (let [timer (timer/create-timer)
            writer (t/writer :json)
            entities-db (prepare-for-indexeddb @db/entities)
            serialized (t/write writer
                                {:data-schema-version @db/data-schema-version
                                 :last-known-update   @db/last-known-update
                                 :last-tenant-plan-ts @db/last-tenant-plan-ts
                                 :last-permissions-ts @db/last-permissions-ts
                                 :entities            entities-db})]
        (logging/debug "Snapshot perf - serialize in "
                       (timer)
                       "ms ["
                       (count entities-db)
                       " entities stored]")
        (.postMessage worker
                      (clj->js {:action          "snapshot-state-response->worker"
                                :debug-ts        now
                                :tenant-id       @session/tenant-id
                                :account-id      @session/account-id
                                :role-id         @session/role-id
                                :serialized-data serialized}))))))

;; Clear sync-reset related timers etc:
(defn clear-sync-reset!
  []
  (let [{:keys [sync-reset-timeout sync-reset-interval]} @session/sync-reset]
    (when sync-reset-timeout
      (js/clearTimeout sync-reset-timeout))
    (when sync-reset-interval
      (js/clearInterval sync-reset-interval))
    (swap! session/sync-reset dissoc
           :reset-sync? :sync-reset-timeout
           :sync-reset-interval :left)))

;; Left for reference:
#_(defn force-sync-reset!
    []
    (let [ctx {:account     @session/account
               :tenant      @session/tenant
               :role-id     @session/role-id
               :role        @session/role
               :target      @session/target
               :reset-sync? true}]
      (clear-sync-reset!)
      (session/clear-db!)
      (login/reset-sync! ctx)))

(defn clear-indexed-db-only!
  []
  (let [ctx {:account @session/account
             :tenant  @session/tenant
             :role-id @session/role-id
             :role    @session/role
             :target  @session/target}]
    (shared-state/clear-indexeddb
     ctx
     (fn [])
     (fn [_]
       (logging/error "Failed to clear IndexedDb")))))

;; Might receive multiple in short succession - don't force immediately
#_(defn start-sync-reset!
    []
    (let [{:keys [reset-sync?]} @session/sync-reset
          timeout-s (* 1000 60 5)
          start-ts (zap-time/now)
          end-ts (+ start-ts timeout-s)
          left timeout-s
          interval-s 1000
          updater (fn []
                    (swap! session/sync-reset assoc
                           :left
                           (int (/ (- end-ts (zap-time/now))
                                   1000))))]
      (when-not reset-sync?
        (swap! session/sync-reset assoc
               :reset-sync? true
               :left (int (/ timeout-s 1000))
               :sync-reset-interval (js/setInterval
                                     updater
                                     interval-s)
               :sync-reset-timeout (fu/timeout
                                    timeout-s
                                     ;;(* 1000 30)
                                    force-sync-reset!)))))

(defn restart-worker-sync!
  [{:keys [token]}]
  (let [{:keys [worker env]} @db/app-state]
    (when worker
      (println "Restart worker with last known update " @db/last-known-update
               "\n" (:account/permissions-updated-on @session/account)
               "\n" (:tenant/plan-updated-on @session/tenant))
      (.postMessage worker
                    (clj->js {:action     "restart-sync->worker"
                              :parameters {:url                 (bu/websocket-url)
                                           :env                 env
                                           :iv                  nil
                                           :native-crypto-key   nil
                                           :reset-sync?         false
                                           :data-schema-version @db/data-schema-version
                                           :last-known-update   @db/last-known-update
                                           :last-permissions-ts (:account/permissions-updated-on @session/account)
                                           :last-tenant-plan-ts (:tenant/plan-updated-on @session/tenant)
                                           :tenant-id           (:db/id @session/tenant)
                                           :account-id          (:db/id @session/account)
                                           :role-id             @session/role-id
                                           :target-id           (:db/id @session/target)
                                           :token               token
                                           :device-id           (device-id/device-id)}})))))

(defn on-worker-message!
  [worker js-msg]
  (let [data (-> js-msg
                 (aget "data")
                 (js->clj :keywordize-keys true))
        now (zap-time/now)]
    ;(js/console.log "WINDOW : worker message received : " (:action data))
    (case (:action data)
      ;;"reset-sync->window" (start-sync-reset!)
      "worker-initialized!->window" (db/update-print-debug-to-worker! (logging/print-debug?))
      "clear-indexeddb->window" (clear-indexed-db-only!)
      "sync-updates->window" (state-updates/process-updates
                              {:reset-session!      account/reset-session
                               :sync-completed!     (partial post-snapshot-data! worker now)
                               :store-to-indexeddb! (partial post-snapshot-data! worker)
                               :timer               (timer/create-timer)
                               :updates             (:updates data)
                               :data-schema-version (:data-schema-version data)
                               :deserialize?        true})
      "worker-disconnected!->window"
      (let [{:keys [worker env]} @db/app-state
            {:keys [token]} @db/session]
        (if (and token
                 (= :open (conn/state))
                 (not (dg/hidden? js/document)))
          (do
            ;; reconnect only if window conn is valid
            (logging/log "Post reconnect message to worker from window thread")
            (restart-worker-sync! {:worker worker
                                   :env    env
                                   :token  token}))
          (logging/log "Worker disconnected -> no active connection, token or window inactive"
                       (= :open (conn/state))
                       (cu/blank-value? token)
                       (dg/hidden? js/document)))))))

(defn sidebar-open->ls
  []
  (let [open? @session/sidebar-open?]
    (ls/set :sidebar-open? open?)))

(defn initialize-sidebar
  []
  (let [sidebar-open-on-start? (ls/get :sidebar-open?)
        sidebar-open-on-start? (cu/default-boolean true sidebar-open-on-start?)
        sidebar-tracker? (contains? @session/session :sidebar-tracker)]
    (swap! session/session assoc :sidebar-open? sidebar-open-on-start?)
    (when-not sidebar-tracker?
      (swap! session/session assoc
             :sidebar-tracker
             (r/track! #'sidebar-open->ls)))))

(defn deactivate-app!
  []
  (println "Deactivate app!")
  (let [{:keys [worker]} @db/app-state]
    (swap! db/app-state assoc :inactive? true)
    (if worker
      (do
        ;; reconnect only if window conn is valid
        (logging/log "Post pause message to worker from window thread")
        (.postMessage worker
                      (clj->js {:action     "pause-sync->worker"
                                :parameters {}})))
      (logging/log "No worker -> no need to pause "))))

(defn reactivate-app!
  []
  (println "Reactivate app!")
  (let [{:keys [worker env]} @db/app-state
        {:keys [token]} @db/session]
    (swap! db/app-state assoc :inactive? false)
    (if (and token
             (= :open (conn/state)))
      (do
        ;; reconnect only if window conn is valid
        (logging/log "Post reconnect message to worker from window thread")
        (restart-worker-sync! {:worker worker
                               :env    env
                               :token  token}))
      (logging/log "Worker disconnected -> no active connection or token "
                   (= :open (conn/state))))))

(defn visibility-changed
  []
  (let [hidden? (dg/hidden? js/document)]
    (if-not (:last-known-update @db/app-state)
      (println "Do not pause/resume due to sync not completed")
      (when (session/has-feature-access? :feature/stop-sync-in-background)
        (if hidden?
          (deactivate-app!)
          (reactivate-app!))))))

(defn init!
  []
  (let [env db/env
        visibility-trigger (cond
                             (exists? (dg/hidden? js/document)) "visibilitychange"
                             (exists? (.-msHidden js/document)) "msvisibilitychange"
                             (exists? (.-webkitHidden js/document)) "webkitvisibilitychange"
                             :else nil)]
    (sentry/init-sentry)
    (logging/update-print-debug! (boolean (ls/get :print-debug?)))
    (.addEventListener js/document visibility-trigger visibility-changed)
    (swap! db/app-state assoc :start-time (c-time/now-millis))
    (device-id/init!)
    (set! (.-onbeforeunload js/window)
          (fn [_]
            (conn/set-state :unloading)
            js/undefined))
    (set! (.-onunload js/window)
          (fn [_]
            (eines-impl/clear-state @eines-impl/state)
            js/undefined))
    (calculate-browser-sizes)
    (chart-view/init-highcharts!)
    (chart/init-highcharts!)
    (check-accesses)
    (dates/initialize-timezone! "Etc/UTC")
    (updates/init!)
    (sente/init!)
    (survey-reference-periods/init-reference-periods)
    (initialize-sidebar)
    (routing/init!)
    (theme/set-theme! themes-registry/capman)
    (tailwind-theme/set-theme! "zapflow")

    (let [worker-filename (if (session/dev?)
                            "/js/worker.js"
                            (str "/js/dynamic/worker-" BUILDTIMEMILLIS ".js"))
          worker (js/Worker.
                  worker-filename)]
      (swap! db/app-state assoc :worker worker)
      (.addEventListener worker "message" (partial on-worker-message! worker))
      #_(js/setInterval (fn []
                          ;; Store latest state when user leaves the application
                          ;; The snapshot is stored also on first date updates set
                          (post-snapshot-data! worker (zap-time/now)))
                        ;; every 10 mins
                        (* 1000 60 10)))
    (conn/init! (bu/websocket-url)
                (partial subscription/dispatch-updates env)
                conn-listener
                on-connected!
                (fn []
                  (logging/info "Logout >> WEBSOCKET EXPIRED")
                  (account/reset-session))
                false)
    (rf/dispatch-sync [::event-handlers/init-app-db])
    (rf/dispatch [::window/track-window-size])))

(defn ^:export set-device
  [device-type]
  ;; iOS app calls this function - we use the :device-type to decide token lifetime at backend and for sending token back to app container
  (let [device-kw (when device-type (keyword device-type))]
    (swap! db/app-state assoc-in [:session :device-type] device-kw)
    (str "" (js/device.ios) " -> " device-kw)))

;; hook for shadow-cljs watch (hot code reload)
(defn ^:dev/after-load refresh
  []
  ;; ensure changes to re-frame subscriptions work immediately:
  (rf/clear-subscription-cache!)
  ;; force re-render so changes are immediately visible:
  (rd/render [main-view] (dg/element-by-id "zap-application")))
